import { authFetch, getSessionToken } from "site/services/auth-session.service";
import { getExerciseImageUrl } from "site/services/content-base-url";
import { subscriptionApiBaseUrl } from "site/services/xq-api-base-url";

export type EditorGalleryItem = {
  id: string;
  path: string;
  tags: string;
  reference_id: string;
  created_by: string;
  created_at: string;
};

function authHeaders(extra: Record<string, string> = {}): Record<string, string> {
  const token = getSessionToken();
  const h: Record<string, string> = { ...extra };
  if (token) {
    h.Authorization = `Bearer ${token}`;
  }
  return h;
}

function apiBase(): string {
  const base = subscriptionApiBaseUrl();
  if (!base) {
    throw new Error("VITE_XQ_API_BASE_URL is not set");
  }
  return base;
}

function galleryPathUrl(path: string): string {
  const segments = String(path ?? "")
    .trim()
    .split("/")
    .filter(Boolean)
    .map((part) => encodeURIComponent(part));
  return `${apiBase()}/editor/gallery/${segments.join("/")}`;
}

export async function fetchEditorGallery(query = ""): Promise<EditorGalleryItem[]> {
  const params = new URLSearchParams();
  if (query.trim()) {
    params.set("q", query.trim());
  }
  const suffix = params.toString() ? `?${params.toString()}` : "";
  const r = await authFetch(`${apiBase()}/editor/gallery${suffix}`, {
    method: "GET",
    headers: authHeaders(),
  });
  if (!r.ok) {
    throw new Error(`editor gallery: HTTP ${r.status}`);
  }
  const data = (await r.json()) as { items?: EditorGalleryItem[] };
  return Array.isArray(data.items) ? data.items : [];
}

export function editorGalleryPreviewUrl(item: EditorGalleryItem, cacheBust = ""): string {
  const base = galleryPathUrl(item.path || item.id);
  const token = String(cacheBust ?? "").trim();
  if (!token) {
    return base;
  }
  return `${base}?v=${encodeURIComponent(token)}`;
}

export function editorGalleryPublicUrl(path: string): string {
  return getExerciseImageUrl(path);
}

/** @deprecated use editorGalleryPublicUrl */
export function editorExerciseImagePreviewUrl(path: string): string {
  return editorGalleryPublicUrl(path);
}

export async function generateEditorGalleryImage(
  prompt: string,
  tags = "",
  replacePath = "",
): Promise<EditorGalleryItem> {
  const body: Record<string, string> = {
    prompt: prompt.trim(),
    tags: tags.trim(),
  };
  const path = String(replacePath ?? "").trim();
  if (path) {
    body.path = path;
  }
  const r = await authFetch(`${apiBase()}/editor/gallery/generate`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(body),
  });
  if (!r.ok) {
    const detail = await r.text().catch(() => "");
    throw new Error(`generate gallery image: HTTP ${r.status}${detail ? ` — ${detail}` : ""}`);
  }
  return (await r.json()) as EditorGalleryItem;
}

export async function uploadEditorGalleryImage(file: File, tags = ""): Promise<EditorGalleryItem> {
  const form = new FormData();
  form.append("file", file);
  if (tags.trim()) {
    form.append("tags", tags.trim());
  }
  const r = await authFetch(`${apiBase()}/editor/gallery/upload`, {
    method: "POST",
    headers: authHeaders(),
    body: form,
  });
  if (!r.ok) {
    const detail = await r.text().catch(() => "");
    throw new Error(`upload gallery image: HTTP ${r.status}${detail ? ` — ${detail}` : ""}`);
  }
  return (await r.json()) as EditorGalleryItem;
}

export async function updateEditorGalleryImageTags(id: string, tags: string): Promise<EditorGalleryItem> {
  const r = await authFetch(galleryPathUrl(id), {
    method: "PATCH",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ tags: tags.trim() }),
  });
  if (!r.ok) {
    const detail = await r.text().catch(() => "");
    throw new Error(`update gallery tags: HTTP ${r.status}${detail ? ` — ${detail}` : ""}`);
  }
  return (await r.json()) as EditorGalleryItem;
}

export async function deleteEditorGalleryImage(id: string): Promise<void> {
  const r = await authFetch(galleryPathUrl(id), {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (r.status === 204) {
    return;
  }
  const detail = await r.text().catch(() => "");
  throw new Error(`delete gallery image: HTTP ${r.status}${detail ? ` — ${detail}` : ""}`);
}

export function formatGalleryPath(path: string): string {
  const normalized = String(path ?? "").trim().replace(/^gallery\//, "");
  const parts = normalized.split("/").filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0]}/${parts[1]}`;
  }
  return normalized;
}
